import type { NotificationTopic } from '@otryad/shared';

/** Строки админки. Тон — как в продукте (BLUEPRINT 3.4), но для операторов допустимы термины кода. */
export const texts = {
  app: {
    title: 'Отряд · Админка',
    short: 'Отряд',
  },
  nav: {
    pool: 'Пул',
    squads: 'Отряды',
    seasons: 'Сезоны',
    venues: 'Площадки',
    activities: 'Активности',
    proposals: 'Предложения',
    users: 'Пользователи',
    orders: 'Заказы',
    notifications: 'Уведомления',
    flags: 'Флаги',
    matching: 'Матчинг',
    seats: 'Места',
    tournaments: 'Турниры',
    metrics: 'Метрики',
    cities: 'Города',
    companies: 'Компании',
    settings: 'Настройки',
    /** Раунд 3, O6: ниже 1024 px разделы открываются панелью по кнопке */
    open: 'Разделы',
    close: 'Закрыть разделы',
  },
  /** Роли словами, а не кодом `OPS`/`ADMIN` (раунд 3, O7). */
  role: {
    MEMBER: 'Участник',
    CAPTAIN: 'Капитан',
    OPS: 'Оператор',
    ADMIN: 'Владелец',
    VENUE: 'Площадка',
  },
  /** Портал площадки (Stage 14, задача 2): отдельный layout под ролью VENUE. */
  venuePortal: {
    nav: {
      bookings: 'Брони',
      stats: 'Статистика',
      settings: 'Площадка',
    },
    title: 'Портал площадки',
    /** Раунд 4, V9: «Портал площадки» — в шапке, заголовок страницы — «Брони» */
    bookingsTitle: 'Брони',
    venueSelect: 'Площадка',
    /** Раунд 4, V7: имя кнопки строки с контекстом брони */
    actionFor: '{action}: {when}, {squad}',
    subtitle: 'Брони отрядов, подтверждение слотов и заполняемость',
    pending: 'Ждут решения: {n}',
    /** Раунд 3, V8: считаются другие брони на это время, без самой строки; число — через `plural` */
    overlap: 'На это время ещё {n}',
    overlapForms: ['бронь', 'брони', 'броней'],
    /** Сумма составов — верхняя граница: столько людей может прийти, если придут все */
    overCapacity: 'До {people} при вместимости {capacity}',
    peopleForms: ['человека', 'человек', 'человек'],
    /** Раунд 3, V3: без ответов вместимость не подставляется */
    going: 'Идут {going} из {n}',
    noAnswers: 'Ответов пока нет',
    inSquadOf: 'в составе {n}',
    durationMin: '{min} мин',
    noVenues: 'К аккаунту не привязана ни одна площадка. Обратись к оператору города.',
    scope: {
      upcoming: 'Впереди',
      pending: 'Ждут решения',
      past: 'Прошедшие',
    },
    columns: {
      when: 'Когда',
      activity: 'Активность',
      squad: 'Отряд',
      people: 'Людей',
      duration: 'Длительность',
      price: 'Слот',
      status: 'Бронь',
    },
    empty: 'Броней нет',
    confirm: 'Подтвердить',
    decline: 'Отклонить',
    declineTitle: 'Отклонить слот',
    declineReason: 'Причина (увидит оператор)',
    /** Раунд 3, V6: в окне отказа видно, какую бронь отклоняют */
    declineWhat: '{when} · {squad}',
    declineSubmit: 'Отклонить слот',
    /** Раунд 4, V3: причина обязательна, частые — одной кнопкой (ключи — `VENUE_DECLINE_REASONS`) */
    declineReasonsLabel: 'Частые причины',
    declineReasons: {
      busy: 'Корт занят на это время',
      closed: 'Площадка закрыта в этот день',
      repair: 'Ремонт или техработы',
      capacity: 'Не вместим столько людей',
    },
    declineReasonRequired:
      'Укажи причину: оператор по ней решает, искать другое время или площадку',
    /** Раунд 4, V2: вернуть отклонённую бронь — второстепенное действие с подтверждением */
    restore: 'Вернуть бронь',
    restoreTitle: 'Вернуть бронь',
    restoreBody:
      '{when} · {squad}. Бронь станет подтверждённой, оператор получит сообщение, что слот снова за отрядом.',
    /** Раунд 4, V1: цена слота записана в бронь, когда она пришла */
    priceFixedHint:
      'Цена слота — по прайсу на момент брони; новая цена часа действует для новых броней',
    note: 'Комментарий',
    status: {
      REQUESTED: 'Ждёт решения',
      CONFIRMED: 'Подтверждена',
      DECLINED: 'Отклонена',
    },
    /** Раунд 3, V4/V5: закрытые брони в «Прошедших» */
    gatheringCancelled: 'Сбор отменён',
    /** Раунд 5, V1/V2: будущий сбор уже закрыт оператором — бронь решения не ждёт */
    gatheringClosedEarly: 'Сбор закрыт оператором',
    noAnswer: 'Без ответа',
    stats: {
      title: 'Заполняемость',
      subtitle:
        'Прошедшие сборы за 12 недель, как в списке «Прошедшие», но без отменённых. Будущие брони — в списке выше',
      empty: 'За 12 недель сборов на площадке не было. Статистика появится после первых проведённых сборов',
      bookings: 'Броней',
      confirmed: 'Подтверждено',
      declined: 'Отклонено',
      noAnswer: 'Без ответа',
      fill: 'Средняя заполняемость',
      fillHint: 'Ответили «иду» к вместимости — по сборам, где кто-то ответил',
      revenue: 'Выручка по прайсу',
      revenueHint:
        'Только подтверждённые слоты, по цене на момент брони; два отряда на одно время — один слот',
      week: 'Неделя',
      weeksLabel: 'По неделям',
      hours: 'Подтверждено часов',
      people: 'Ответили «иду»',
      /** Раунд 3, V5: «14–20 сент.» вместо 2026-09-14 и «1 ч 30 мин» вместо 1.5 */
      hoursValue: '{h} ч {m} мин',
      hoursOnly: '{h} ч',
      minutesOnly: '{m} мин',
    },
    settings: {
      title: 'Площадка',
      subtitle: 'Цена часа, непиковые окна и контакт',
      price: 'Цена часа, ₽',
      capacity: 'Вместимость',
      capacityHint: 'Человек за слот — по нему видно наложение броней',
      capacityInvalid: 'Вместимость — целое число от 1 до 999. Пустое поле снимает вместимость',
      priceInvalid: 'Цена часа — число в рублях, например 6000 или 1500,50. Пустое поле снимает цену',
      contact: 'Контакт',
      contactHint: 'Телефон или чат, по которому с площадкой связывается оператор',
      activities: 'Что принимаем',
      activitiesHint:
        'Ничего не отмечено — оператор не сможет поставить сюда сбор. Отметь, что площадка принимает',
      /** Раунд 3, V9: подсказка следует за отметками */
      activitiesOnly: 'Заявки приходят только по отмеченным: {list}',
      offPeak: 'Непиковые окна',
      offPeakHint: 'Часы со скидкой, в которые площадка ждёт отряды',
      offPeakEmpty: 'Окон нет',
      /** Раунд 3, V10: у полей окна есть подписи и имена */
      offPeakWeekday: 'День',
      offPeakFrom: 'С',
      offPeakTo: 'До',
      offPeakDiscount: 'Скидка, %',
      offPeakRow: 'Окно {n}',
      offPeakInvalid: 'Окно {n}: время в формате 18:00, начало раньше конца, скидка от 0 до 100',
      removeWindow: 'Убрать окно {n}',
      addWindow: 'Добавить окно',
      save: 'Сохранить',
      saved: 'Сохранено',
    },
  },
  /** Города и открытие набора (Stage 14, задача 1). */
  cities: {
    title: 'Города',
    subtitle: 'Районы, часовой пояс, оператор и чек-лист открытия набора',
    /** Раунд 5, O5: оператор видит города и чек-лист, но заводит, правит и открывает их владелец */
    readOnly:
      'Заводить и править города, открывать набор и назначать операторов может только владелец. Чек-лист открыт для просмотра',
    add: 'Новый город',
    empty: 'Городов нет',
    columns: {
      name: 'Город',
      timezone: 'Часовой пояс',
      districts: 'Районы',
      venues: 'Площадки',
      squads: 'Отряды',
      pool: 'В пуле',
      operators: 'Оператор',
      status: 'Набор',
    },
    opened: 'Открыт',
    closed: 'Закрыт',
    open: 'Открыть город',
    close: 'Закрыть набор',
    openConfirm: 'Открыть набор в городе «{city}»?',
    closeConfirm: 'Закрыть набор в городе «{city}»? Отряды продолжат играть.',
    checklist: {
      title: 'Чек-лист открытия',
      soft: 'предупреждение',
      have: '{have} из {need}',
      enough: '{have} · нужно от {need}',
      notNeeded: 'не требуется',
      steps: {
        districts: 'Заведены районы',
        venues: 'Заведены площадки',
        activities: 'Есть основные активности',
        templates: 'Есть шаблон сезона',
        pricing: 'У площадки указана цена часа',
        operator: 'Назначен оператор города',
        proven: 'Первый город отработан',
      },
    },
    form: {
      createTitle: 'Новый город',
      editTitle: 'Город',
      name: 'Название',
      slug: 'Код (латиницей)',
      timezone: 'Часовой пояс',
      districts: 'Районы',
      districtName: 'Название',
      districtSlug: 'Код',
      neighbors: 'Соседи (коды через запятую)',
      addDistrict: 'Добавить район',
      removeDistrict: 'Убрать',
      active: 'Город активен',
      save: 'Сохранить',
      create: 'Завести город',
    },
    operator: {
      title: 'Оператор города',
      user: 'Пользователь',
      searchPlaceholder: 'Имя, @username или Telegram ID',
      searchHint: 'Участник станет оператором; владелец останется владельцем',
      change: 'Другой',
      set: 'Назначить',
      hint: 'Пустой список городов — оператор видит все города',
      none: 'не назначен',
    },
  },
  /** Компании и корпоративные сезоны (Stage 14, задача 4). */
  companies: {
    title: 'Компании',
    subtitle: 'Корпоративные сезоны: промокод сотрудника, счёт и приоритетный пул',
    add: 'Новая компания',
    empty: 'Компаний нет',
    showInactive: 'Показывать неактивные',
    columns: {
      name: 'Компания',
      city: 'Город',
      promo: 'Промокод',
      discount: 'Скидка',
      seats: 'Мест куплено',
      employees: 'Сотрудников',
      pool: 'В пуле',
      placed: 'В отрядах',
      paid: 'Оплачено',
      mode: 'Отряды',
      active: 'Активна',
    },
    mixed: 'Смешанные',
    employeesOnly: 'Только сотрудники',
    form: {
      createTitle: 'Новая компания',
      editTitle: 'Компания',
      name: 'Название',
      city: 'Город',
      inn: 'ИНН',
      contact: 'Контакт',
      promo: 'Промокод (пусто — сгенерируем)',
      discount: 'Скидка сотруднику, %',
      seats: 'Мест оплачено',
      mixedSquads: 'Сотрудники попадают в общие отряды города',
      active: 'Компания активна',
      notes: 'Заметки',
      save: 'Сохранить',
      create: 'Завести компанию',
    },
    invoice: {
      title: 'Счёт компании',
      seats: 'Мест',
      amount: 'Сумма, ₽',
      paid: 'Счёт уже оплачен по банку',
      submit: 'Выставить счёт',
      hint: 'Счёт выставляется вне системы; здесь фиксируется факт',
    },
    leads: {
      title: 'Заявки с лендинга',
      empty: 'Заявок нет',
      company: 'Компания',
      contact: 'Контакт',
      city: 'Город',
      people: 'Людей',
      comment: 'Комментарий',
      when: 'Когда',
      create: 'Завести',
    },
  },
  theme: {
    label: 'Тема',
    auto: 'Авто',
    light: 'Светлая',
    dark: 'Тёмная',
  },
  auth: {
    loginTitle: 'Вход для операторов',
    /** Раунд 4, O15: роли словами, как в `role`, а не кодом ops/admin */
    loginBody: 'Войди через Telegram. Доступ — у операторов и владельца',
    widgetUnavailable: 'Виджет не настроен: в API не задан BOT_USERNAME',
    checking: 'Проверяю доступ',
    forbiddenTitle: 'Нет доступа',
    forbiddenBody: 'Аккаунт вошёл, но он не оператор и не владелец. Попроси владельца выдать роль',
    errorForbidden: 'Доступ в админку — у операторов и владельца',
    errorBadSignature: 'Telegram не подтвердил вход. Попробуй ещё раз',
    errorNotConfigured: 'API не настроен: нет BOT_TOKEN',
    errorNetwork: 'Нет связи с API',
    devTitle: 'Dev-вход (AUTH_ALLOW_DEV_MOCK)',
    devBody: 'Вход по Telegram ID без виджета. Работает только вне production',
    devTgId: 'Telegram ID',
    devSubmit: 'Войти',
    logout: 'Выйти',
    role: 'Роль',
  },
  common: {
    /** Раунд 4, O3: срок оплаты у сезона, который уже вышел из продажи */
    deadlineInactive: 'Не действует: продажи закрыты',
    loading: 'Загружаю',
    retry: 'Повторить',
    error: 'Что-то пошло не так',
    search: 'Поиск',
    loadMore: 'Показать ещё',
    total: 'Всего',
    empty: 'Ничего не найдено',
    all: 'Все',
    yes: 'да',
    no: 'нет',
    save: 'Сохранить',
    /** Раунд 3, O7: «Отмена» рядом с «Отменить» читалось как два одинаковых действия */
    cancel: 'Закрыть',
    close: 'Закрыть',
    confirmTitle: 'Подтверждение',
    create: 'Создать',
    edit: 'Изменить',
    add: 'Добавить',
    remove: 'Убрать',
    apply: 'Применить',
    reset: 'Сбросить',
    back: 'Назад',
    open: 'Открыть',
    notSet: '—',
    /** Раунд 4, O8: обезличенный аккаунт вместо отрицательного Telegram ID */
    deletedAccount: 'аккаунт удалён',
    details: 'Подробности',
    saved: 'Сохранено',
    stage: 'Раздел появится в Stage {stage}',
    of: 'из',
    from: 'от',
    to: 'до',
    city: 'Город',
    activity: 'Активность',
    district: 'Район',
    districts: 'Районы',
    weekday: 'Вечер',
    weekdays: 'Вечера',
    level: 'Уровень',
    age: 'Возраст',
    size: 'Размер',
    status: 'Статус',
    actions: 'Действия',
    created: 'Создано',
    name: 'Название',
    description: 'Описание',
    comment: 'Комментарий',
    selected: 'Выбрано',
    confirm: 'Подтвердить',
    rubles: '₽',
    minutes: 'мин',
    people: 'чел.',
    id: 'ID',
    copy: 'Скопировать',
    copied: 'Скопировано',
  },
  users: {
    title: 'Пользователи',
    searchPlaceholder: 'Имя, username или Telegram ID',
    roleFilter: 'Роль',
    /** Раунд 4, O13 */
    statusFilter: 'Статус аккаунта',
    statusFilters: {
      live: 'Без удалённых',
      ACTIVE: 'Активные',
      BANNED: 'Заблокированные',
      DELETED: 'Удалённые',
      all: 'Все статусы',
    },
    columns: {
      name: 'Имя',
      username: 'Username',
      tgId: 'Telegram ID',
      role: 'Роль',
      status: 'Статус',
      city: 'Город',
      onboarded: 'Онбординг',
      reliability: 'Надёжность',
      streak: 'Серия',
      createdAt: 'Создан',
    },
  },
  user: {
    title: 'Пользователь',
    notFound: 'Пользователь не найден',
    profile: 'Профиль',
    phone: 'Телефон',
    birthYear: 'Год рождения',
    availability: 'Свободные вечера',
    onboardedAt: 'Онбординг пройден',
    agreedRulesAt: 'Правила приняты',
    referralCode: 'Реферальный код',
    referredBy: 'Привёл',
    reliability: 'Надёжность',
    streak: 'Серия явок',
    levels: 'Уровни',
    levelSelf: 'Самооценка',
    levelAdjusted: 'Корректировка',
    levelNoAdjust: 'без правки',
    levelsEmpty: 'Уровней пока нет',
    memberships: 'Отряды',
    membershipsEmpty: 'В отрядах не состоит',
    applications: 'Заявки в пуле',
    applicationsEmpty: 'Заявок нет',
    proposals: 'Предложения активностей',
    proposalsEmpty: 'Предложений нет',
    notes: 'Заметки оператора',
    notesPlaceholder: 'Что важно знать оператору: как связаться, договорённости, нюансы',
    notesSaved: 'Заметки сохранены',
    role: 'Роль',
    roleHint: 'Роли меняет только владелец',
    ban: 'Заблокировать',
    unban: 'Разблокировать',
    banReason: 'Причина блокировки',
    banConfirm: 'Заблокировать пользователя? Заявки снимутся, из отрядов он будет выведен',
    unbanConfirm: 'Разблокировать пользователя?',
    audit: 'Журнал действий',
    auditEmpty: 'Записей нет',
    captain: 'капитан',
    reserve: 'запасной',
  },
  pool: {
    title: 'Пул',
    subtitle: 'Кандидаты по ячейкам: город × активность × размер × вечер × уровень',
    cells: 'Ячейки',
    cellsEmpty: 'В пуле пусто по этим фильтрам',
    candidates: 'Кандидаты',
    candidatesEmpty: 'Кандидатов нет',
    allCells: 'Все ячейки',
    progress: '{count} из {size}',
    startFrom: 'старт от {min}',
    build: 'Собрать отряд',
    buildCount: 'Собрать отряд ({count})',
    selectHint: 'Отметь кандидатов в таблице и нажми «Собрать отряд»',
    /** Раунд 4, O6: возраст и приоритет — строками в соседних колонках */
    ageLine: '{age} лет · {band}',
    priorityBadge: 'приоритет +{n}',
    filters: {
      city: 'Город',
      activity: 'Активность',
      size: 'Размер',
      weekdays: 'Вечера',
      level: 'Уровень',
      ageBand: 'Возраст',
      district: 'Район',
      status: 'Статус заявки',
    },
    columns: {
      name: 'Кандидат',
      age: 'Возраст',
      level: 'Уровень',
      districts: 'Районы',
      days: 'Вечера',
      window: 'Окно',
      priority: 'Приоритет',
      reliability: 'Надёжность',
      createdAt: 'В пуле с',
      size: 'Размер',
      activity: 'Активность',
    },
  },
  squadForm: {
    title: 'Новый отряд',
    editTitle: 'Параметры отряда',
    /** Раунд 4, O4: смена вечера при живом сезоне */
    seasonScheduleWarning:
      'У отряда идёт сезон, его сборы стоят на прежнем вечере. Сохранение не пройдёт, пока сборы не перенесены в конструкторе сезона (там проверки дат и новая бронь площадке) — или отметь, что меняется только отряд',
    keepSeasonSchedule:
      'Только отряд: сборы сезона остаются на своих датах, новый вечер — со следующего сезона',
    activity: 'Основная активность',
    capacity: 'Размер отряда',
    weekday: 'Вечер',
    startTime: 'Время начала',
    levelBand: 'Полоса уровня',
    ageMin: 'Возраст от',
    ageMax: 'Возраст до',
    districts: 'Районы',
    name: 'Название (необязательно)',
    composition: 'Состав',
    minToLaunch: 'Минимум для запуска',
    minToLaunchAuto: 'авто: {value}',
    reserveCapacity: 'Мест для запасных',
    reserveAuto: 'по активности: {value}',
    captain: 'Капитан',
    noCaptain: 'Назначить позже',
    members: 'Состав из пула',
    membersEmpty: 'Отряд без участников — добавишь позже со страницы отряда',
    submit: 'Создать отряд',
    overCapacity: 'Выбрано больше людей, чем мест: {selected} из {capacity}',
    mixedActivity: 'Выбраны кандидаты из разных активностей или размеров — оставь одну ячейку',
    scheduleConflict:
      'Вечер или время не подходят {count} из {total} по заявке: {names}. Уточни у них или выбери другое время',
  },
  squads: {
    title: 'Отряды',
    subtitle: 'Ручная сборка, состав, продажи, запуск',
    empty: 'Отрядов пока нет. Собери первый из пула',
    statusFilter: 'Статусы',
    openOnly: 'Только открытые',
    /** Раунд 3, O7 */
    belowMin: 'Ниже минимума {min}',
    columns: {
      squad: 'Отряд',
      city: 'Город',
      evening: 'Вечер',
      level: 'Уровень',
      ages: 'Возраст',
      roster: 'Состав',
      captain: 'Капитан',
      status: 'Статус',
      saleDeadline: 'Дедлайн оплаты',
      createdAt: 'Создан',
    },
  },
  squad: {
    title: 'Отряд',
    notFound: 'Отряд не найден',
    untitled: '{activity} · {weekday} {time}',
    untitledId: '{base} · #{tail}',
    info: 'Параметры',
    roster: 'Состав',
    rosterCount: '{count} из {capacity}',
    reserveCount: 'запасных {count} из {capacity}',
    minToLaunch: 'минимум для запуска {value}',
    rosterEmpty: 'Состава пока нет',
    addFromPool: 'Добавить из пула',
    addFromPoolTitle: 'Кандидаты из пула',
    addFromPoolEmpty: 'В пуле нет подходящих кандидатов',
    addAsMember: 'В состав',
    addAsReserve: 'Запасным',
    makeCaptain: 'Сделать капитаном',
    captain: 'Капитан',
    reserve: 'Запасной',
    removeTitle: 'Убрать из состава',
    removeBody: 'Убрать {name} из отряда?',
    returnToPool: 'Вернуть заявку в пул',
    openSale: 'Открыть продажи',
    openSaleConfirm:
      'Открыть продажи? Статус станет «Продажи», участники получат сообщение, срок оплаты — через {days} дней',
    launch: 'Запустить',
    launchConfirm: 'Запустить отряд вручную без проверки оплат? Статус станет «Активен»',
    /** Раунд 3, O7: «Запустить» неактивна, пока в составе меньше минимума */
    launchBelowMin: 'В составе {n}, для запуска нужно не меньше {min}',
    disband: 'Распустить',
    disbandTitle: 'Распустить отряд',
    disbandBody:
      'Все участники будут выведены из состава, живой сезон отряда отменится вместе с предстоящими сборами. Действие необратимо',
    disbandReturn: 'Вернуть участников в пул с приоритетом +1',
    telegram: 'Группа Telegram',
    tgChatId: 'Chat ID',
    tgInviteLink: 'Ссылка-приглашение',
    tgHint: 'Или добавь бота в группу отряда и отправь там /bind — капитан или оператор',
    tgSaved: 'Группа привязана',
    saleDeadline: 'Дедлайн оплаты',
    audit: 'Журнал',
    auditEmpty: 'Записей нет',
    phone: 'Телефон',
    level: 'Уровень',
    days: 'Вечера',
    capacity: 'Размер',
    composition: 'Состав',
    season: 'Сезон',
    seasonEmpty: 'Сезона ещё нет. Собери его из шаблона: 6 сборов, площадки, даты',
    seasonCreate: 'Создать сезон',
    seasonOpen: 'Открыть сезон',
    seasonNumber: 'Сезон {number}',
    seasonDates: '{from} — {to}',
    seasonProgress: 'проведено {done} из {total} сборов',
    /** Раунд персон 2, O2: доп. сборы отдельно, как на экране сезона (`seasonProgress`) */
    seasonProgressExtra: 'проведено {done} из {total} сборов + {extra} доп.',
    seasonHistory: 'Прошлые сезоны',
    edit: 'Изменить параметры',
  },
  seasons: {
    title: 'Сезоны',
    subtitle: 'Сезоны отрядов: статус, даты, сборы',
    empty: 'Сезонов пока нет. Создай сезон со страницы отряда',
    openOnly: 'Только открытые',
    columns: {
      squad: 'Отряд',
      number: '№',
      status: 'Статус',
      dates: 'Даты',
      gatherings: 'Сборы',
      next: 'Ближайший',
      price: 'Цена',
      saleDeadline: 'Дедлайн оплаты',
    },
  },
  builder: {
    title: 'Конструктор сезона',
    newTitle: 'Новый сезон',
    forSquad: 'Отряд',
    noSquad: 'Не указан отряд: открой конструктор со страницы отряда',
    template: 'Шаблон',
    startsOn: 'Первый сбор не раньше',
    startsOnHint: 'Сборы встанут на ближайшие вечера отряда начиная с этой даты',
    price: 'Цена сезона, ₽',
    priceHint: 'Пусто — цена активности, а если её нет — «Сезон из 6 сборов» из настроек',
    preview: 'Показать сборы',
    previewTitle: 'Сборы сезона',
    previewHint: 'Поправь активность, площадку, дату и время каждого сбора, затем сохрани сезон',
    save: 'Сохранить сезон',
    saved: 'Сезон создан',
    columns: {
      index: '№',
      /** Раунд 4, O6: активность, дата, соперник и заметки — в одной колонке */
      gathering: 'Сбор',
      activity: 'Активность',
      venue: 'Площадка',
      date: 'Дата',
      time: 'Время',
      duration: 'Длит., мин',
      minAttendance: 'Мин. явка',
      attendance: 'Явка',
      attendanceMin: 'от {n}',
      rsvpYesShort: 'идут {n}',
      opponent: 'Соперник',
      notes: 'Заметки',
      status: 'Статус',
      rsvp: 'Идут',
    },
    noVenue: 'без площадки',
    noOpponent: 'без соперника',
    warnings: {
      no_venue: 'В городе нет площадки с этой активностью',
      two_rounds: 'Площадка меньше состава: два захода',
      venue_pending: 'Площадку для активности ещё ищем',
    },
    /** Раунд 3, O2: предупреждение, не запрет — сбор можно сохранить */
    notSquadEvening: 'Не вечер отряда ({weekday} {time}) — участники могли не планировать',
    /** Раунд 4, O12 */
    notSquadEveningShort: 'не вечер отряда',
    gatheringPast: 'Сбор {index} в прошлом — поставь дату и время впереди',
    namedPast: '{name} в прошлом — поставь дату и время впереди',
    opponentSameEvening: 'Тот же вечер ({weekday})',
    opponentOtherEvening: 'Другие вечера — турнир потребует переноса',
    gatherings: 'Сборы',
    seasonActions: 'Сезон',
    openSale: 'Открыть продажи',
    openSaleConfirm:
      'Открыть продажи сезона? Отряд перейдёт в «Продажи», участники получат сообщение, срок оплаты — по окну продаж из настроек',
    launch: 'Запустить сезон',
    launchConfirm: 'Запустить сезон? Оплатили {paid} из {min}, нужных для запуска. Сезон станет «Идёт», отряд — «Активен»',
    /** Раунд 4, O10: черновик запускается без продаж — по составу, оплаты не проверяются */
    launchConfirmDraft:
      'Запустить сезон без продаж? В составе {n}, минимум {min}. Оплаты у черновика не проверяются. Сезон станет «Идёт», отряд — «Активен»',
    /** Раунд 4, O10: почему «Запустить сезон» неактивна — те же правила, что на сервере */
    launchBlocked: {
      not_enough_members: 'Запуск не пройдёт: в составе {n}, нужно не меньше {min}',
      not_enough_paid: 'Запуск не пройдёт: оплатили {paid}, нужно не меньше {min}',
    },
    paid: 'Оплатили',
    /** Раунд 3, O7: «6 из 6 (состав 8)» читалось как «оплатили все» — делим на три части */
    paidOf: '{paid} · для запуска нужно {min} · мест в отряде {members}',
    extendDeadline: 'Продлить дедлайн',
    extendTitle: 'Продлить дедлайн оплаты',
    extendBody: 'Сейчас дедлайн {deadline}. Участники получат сообщение, отряд получит тот же срок. Продлений осталось: {left}',
    extendDays: 'На сколько дней',
    extendDone: 'Дедлайн продлён',
    extendUsed: 'Дедлайн уже продлевали ({n} из {max})',
    deadlineJobHint: 'В дедлайн планировщик запустит сезон при достаточных оплатах или вернёт деньги и распустит отряд',
    cancel: 'Отменить сезон',
    cancelTitle: 'Отменить сезон',
    cancelBody: 'Все предстоящие сборы будут отменены. Отряд останется в текущем статусе',
    /** Что отмена сезона или роспуск сделают с деньгами и сборами (раунд 3, O1) */
    cancellation: {
      loading: 'Считаем, что будет со сборами и деньгами…',
      error: 'Не получилось посчитать возвраты — не подтверждайте, пока не увидите сумму',
      noSeason: 'Живого сезона у отряда нет — сборы и оплаты не затрагиваются',
      seasons: 'Отменится сезонов: {n} (идущий и продление)',
      gatherings: 'Отменится сборов: {n}. Прошедшие остаются в истории',
      noGatherings: 'Открытых сборов нет',
      refund: 'К возврату {amount}, оплат: {orders}',
      refundRule:
        'Сезон в продаже возвращается целиком, идущий — за несостоявшиеся сборы; замена на отменённый сбор — целиком',
      noRefund: 'Возвращать нечего: оплат за предстоящие сборы нет',
      manual:
        'Робокасса и переводы не возвращают деньги сами. Система отметит возврат как внешний и напишет участникам, а деньги нужно вернуть в личном кабинете Робокассы или переводом — список в «Заказах», статусы «Возвращён» и «Частичный возврат»',
      auto: 'Деньги вернутся через платёжную систему, участники получат сообщение',
      pending: 'Неоплаченных счетов закроется: {n}',
      /** Раунд 4, O5: отмена одного сбора — правило из runbook «Возврат» */
      gatheringRule:
        'Каждому оплатившему сезон вернётся доля за этот сбор: сумма оплаты, делённая на число сборов, которые она покрывает. Замена или пробный на этот сбор — целиком',
      noGatheringRefund: 'Возвращать нечего: за этот сбор никто не платил',
      /** Stage 17, задача 5: позиции заявок не возвращаются сразу */
      bookingItems:
        'Из них позиций заявок на разовые места: {n} на {amount}. Они уйдут в «ждут возврата»: верни деньги в кабинете Робокассы и нажми «Возврат» в «Заказах»',
      /** Stage 17, задача 6 (ADR-44): перенос сбора и оплаченные записи гостей */
      rescheduleSquad: 'Состав и оплаты сезона переезжают на новую дату — их деньги не двигаются',
      rescheduleGuests: 'Записи гостей не переезжают: к возврату {amount}, оплат: {orders}',
      rescheduleRule:
        'Разовые места, замены и пробные куплены на этот день и час — они снимаются и возвращаются целиком. На новую дату человек может записаться заново',
      noRescheduleGuests: 'Оплаченных разовых мест, замен и пробных на этот сбор нет',
    },
    cancelReason: 'Причина (увидят участники)',
    reschedule: 'Перенести',
    rescheduleTitle: 'Перенести сбор',
    rescheduleBody: 'Создастся новый сбор с тем же номером, старый будет помечен как отменённый',
    rescheduleReason: 'Причина переноса',
    cancelGathering: 'Отменить сбор',
    cancelGatheringTitle: 'Отменить сбор',
    cancelGatheringReason: 'Причина (увидят участники)',
    cancelGatheringBody: 'Сбор отменится у всего состава, участники получат сообщение с причиной',
    /** Раунд персон 1, O11: почему «Отменить сбор» неактивна */
    cancelGatheringNeedReason: 'Укажи причину — без неё сбор не отменить',
    /** Раунд 4, O1: бронь площадки у каждого сбора сезона */
    venueStatus: {
      REQUESTED: 'Площадка: ждёт ответа',
      CONFIRMED: 'Площадка подтвердила',
      DECLINED: 'Площадка отказала',
    },
    venueDeclinedNote: 'Причина: {note}',
    venueDeclinedHint: 'Поставь другую площадку («Изменить») или перенеси сбор',
    /** Раунд 4, O2: сбор закончился, а его не закрыли */
    overdue: 'Прошёл, не закрыт',
    overdueHint: 'Сбор уже закончился: заверши его или отмени',
    overdueCount: 'Прошедших незакрытых сборов: {n}',
    confirm: 'Подтвердить',
    complete: 'Завершить',
    editGathering: 'Изменить',
    /**
     * Раунд персон 2, O3: имя сбора в окнах и подписях кнопок. Доп. сбор — без внутреннего номера
     * (его номер после шестого ничего не значит), по дате: сборов ops в сезоне может быть два
     */
    gatheringName: 'Сбор {index}',
    extraName: 'Доп. сбор {date}',
    /** Колонка «№» у доп. сбора */
    extraIndex: 'доп.',
    editGatheringTitle: '{name}',
    /** Раунд 4, O6: второстепенные действия сбора — в меню «…» */
    gatheringMenu: '{name}: ещё действия',
    confirmFor: 'Подтвердить: {name}',
    completeFor: 'Завершить: {name}',
    /**
     * Раунд персон 2, O1: подтверждение тоже необратимо (из «Подтверждён» назад пути нет) и
     * обычно делается само — окно, как у «Завершить»
     */
    confirmTitle: 'Подтвердить: {name}?',
    confirmConfirm:
      '{name} ({when}) станет «Подтверждён» — участники увидят, что сбор точно будет. Обычно сбор подтверждается сам за 24 часа, когда «иду» набирается от {min}. Вернуть в «Запланирован» нельзя, только отменить',
    /** Раунд 5, O1: завершение необратимо — пересчёт надёжности, продление, метрики */
    completeTitle: 'Завершить: {name}?',
    completeConfirm:
      '{name} ({when}) станет «Проведён». Явка пойдёт в надёжность участников, отменить это нельзя',
    completeNotStarted: 'Завершить можно после начала сбора',
    completeNotActive: 'Завершить можно, когда сезон идёт',
    opponentLine: 'Соперник: {squad}',
    replacedBy: 'перенесён',
    audit: 'Журнал',
    auditEmpty: 'Записей нет',
    info: 'Параметры',
    squadStatus: 'Статус отряда',
    saleOpensAt: 'Продажи открыты',
    /** Stage 17, задача 5: разовый сбор ops в идущем сезоне (ADR-35, ADR-43) */
    /** Раунд персон 2, O3: сбор ops везде «доп. сбор» — как в Mini App и в боте (ADR-51 п. 4) */
    addGathering: 'Добавить доп. сбор',
    addGatheringBlocked: {
      feature_disabled: 'Доп. сбор выключен: «Мои сборы» (feature.myGatherings) в настройках',
      season_not_active: 'Доп. сбор добавляется только в идущий сезон',
    },
    /**
     * Раунд персон 1 (решение по словам, O9): «разовый» — только человек на чужом месте; сбор вне
     * шаблона — доп. сбор, от ops или по опросу отряда (Stage 12)
     */
    extraBadge: 'Доп. сбор · ops',
    extraBadgePoll: 'Доп. сбор',
    capacityLine: 'Вместимость {n}',
    singlesClosedLine: 'Капитан закрыл сбор для разовых',
    formatLine: 'Формат: {format}',
    formatDefault: 'не задан, игрок видит «до конца»',
    /** Раунд персон 1, O11: значение после «Формат:» — со строчной */
    formatValue: {
      STANDARD: 'до конца',
      KNOCKOUT: 'на выбывание',
    },
    /** Раунд персон 1, O10: сезон по шаблону без отменённых, доп. сборы отдельно (`seasonProgress`) */
    progress: 'проведено {done} из {total}',
    progressExtra: 'проведено {done} из {total} + {extra} доп.',
    add: {
      title: 'Добавить доп. сбор',
      body: 'Сбор отряда вне шаблона сезона: в цену сезона не входит, состав платит на месте. Разовые места на нём продаются по общим правилам',
      activity: 'Активность',
      date: 'Дата',
      /**
       * Раунд персон 1, O5: окно — то же, что проверяет сервер (`extraGatheringWindow`). Раунд
       * персон 2, O7: окно не раньше сегодня; подсказка видна и рядом с ошибкой
       */
      dateHint: 'Можно с {from} по {to}: сезон ± неделя, не раньше сегодня',
      dateRequired: 'Укажи дату сбора',
      dateOutOfWindow: 'Дата вне окна сезона',
      dateInPast: 'Эта дата уже прошла',
      timeInPast: 'Это время сегодня уже прошло',
      windowEmpty: 'Окно сезона ± неделя уже закончилось — доп. сбор в этот сезон не поставить',
      time: 'Время',
      timezone: 'Время города, {tz}',
      duration: 'Длительность, мин',
      durationHint: 'По умолчанию — длительность активности',
      venue: 'Площадка',
      venueLater: 'подобрать позже — бронь площадке не уйдёт',
      /** Раунд персон 1, O4: площадка меньше отряда в списке, но не выбирается */
      venueTooSmall: '{name} · {capacity} — меньше отряда',
      capacity: 'Вместимость сбора',
      capacityHint: 'От {min} (размер отряда) до {max} (площадка)',
      capacityHintNoMax: 'От {min} (размер отряда); у площадки нет потолка',
      capacityHintNoVenue: 'Без площадки — только размер отряда ({min})',
      /** Раунд персон 2, O6: ошибка называет границы, а не «разовых мест 0» */
      capacityErrorRange: 'Вместимость — целое число от {min} (размер отряда) до {max} (площадка)',
      capacityErrorMin: 'Вместимость — целое число не меньше {min} (размер отряда)',
      capacityErrorExact:
        'Вместимость — только {min}: в «{venue}» столько же мест, сколько в отряде. Выбери площадку больше, чтобы поднять её',
      capacityHintExact: 'Площадка на {min} — как отряд: вместимость только {min}',
      capacityErrorNoVenue:
        'Без площадки вместимость — только {min} (размер отряда). Выбери площадку, чтобы поднять её',
      /**
       * Раунд персон 2, O6: откуда разовые места (`singleSeatsFor`): не больше доли вместимости, и
       * только на свободные места — сверх размера отряда или там, где в составе пусто или
       * ответили «не иду»
       */
      singles: 'Разовых и пробных — до {n}: {pct} % от вместимости {capacity}',
      singlesBeyond:
        'Все {n} — сверх размера отряда: их можно продать, даже если придёт весь состав',
      singlesBeyondPart:
        'Сверх размера отряда — {beyond}; остальные — только когда в составе есть свободные места или кто-то ответит «не иду»',
      singlesNone: 'Разовых мест не будет: {pct} % от {capacity} — меньше одного места',
      singlesNoBeyond:
        'Вместимость равна отряду: разовые места появятся, только когда в составе есть свободные места или кто-то ответит «не иду». Чтобы продавать их наверняка, подними вместимость',
      singlePrice: 'Цена разового места: {price} —',
      /** Раунд персон 1, O8: ссылка на саму настройку множителя и её группу */
      singlePriceLink: '«{setting}» в настройках, группа «{group}»',
      format: 'Формат турнира',
      formatHint: 'До конца — игрок участвует весь турнир; на выбывание — выбывший может уйти',
      notes: 'Заметка для состава',
      notesHint: 'К заметке добавится «оплата на месте»',
      preview: 'Предпросмотр: {when} · {activity} · разовых мест до {n} · {price}',
      previewNoCapacity: 'Предпросмотр: {when} · {activity} · вместимость не задана',
      submit: 'Добавить доп. сбор',
    },
  },
  /** Очередь флагов (Stage 9, задача 6) */
  /** `/seats` (Stage 11, задача 5): открытые места, кому предложено, ручное предложение. */
  tournaments: {
    title: 'Турниры',
    /** Раунд персон 1, O18: турнир — любой сбор-турнир сезона (слот шаблона или доп. сбор ops), не «шестой» */
    subtitle: 'Сборы-турниры сезонов: соперник того же города, активности и уровня ±1, формат турнира',
    empty: 'Сборов-турниров в открытых сезонах нет',
    unnamed: 'Отряд без названия',
    noCandidates: 'Кандидатов нет',
    suggested: 'Предложение: {name}, уровень {level}',
    pick: 'Подобрать',
    pickTitle: 'Соперник на турнир',
    pickHint:
      'Связка двусторонняя: у обоих отрядов сбор станет общей карточкой, счёт вносит капитан',
    assign: 'Назначить',
    /** Раунд 3, O7 */
    otherDate: 'Турнир соперника {theirs}, у этого отряда — {ours}: перенеси один из сборов, иначе отряды не встретятся',
    clear: 'Снять',
    columns: {
      when: 'Когда',
      squad: 'Отряд',
      level: 'Уровень',
      opponent: 'Соперник',
      /** Stage 17, задача 5 */
      format: 'Формат',
      result: 'Счёт',
    },
    /** Формат турнира (Stage 17, задача 5): пусто читается как «Обычный» */
    formats: {
      none: 'Не задан',
      STANDARD: 'До конца',
      KNOCKOUT: 'На выбывание',
    },
    formatFor: 'Формат турнира: {squad}',
    formatLock: {
      gathering_closed: 'Турнир начался или закрыт',
      has_bookings: 'На турнир записались — не меняется',
    },
    formatPair: 'Меняется у обоих отрядов',
    /** Раунд персон 2, O3 */
    opsCreated: 'доп. сбор',
  },
  metrics: {
    title: 'Метрики',
    subtitle: 'Пять метрик продукта, воронка и ряды по неделям',
    filters: {
      city: 'Город',
      period: 'Период',
      allCities: 'Все города',
    },
    periods: {
      '30': '30 дней',
      '90': '90 дней',
      '180': 'полгода',
      '365': 'год',
    },
    scope: 'Данные с {from} по {to} · событий за период: {events}',
    /** Первая строка отчёта владельцу: неделя словами, а не `2026-09-14` */
    reportHead: '{city}, неделя {week}',
    /** Юнит-экономика города (Stage 14, задача 5). */
    unit: {
      title: 'Юнит-экономика',
      subtitle: 'Выручка, стоимость площадок по прайсу и маржа на отряд за период',
      revenue: 'Выручка',
      venueCost: 'Площадки',
      margin: 'Маржа',
      marginPct: 'Маржа, %',
      perSquad: 'Маржа на отряд',
      squad: 'Отряд',
      gatherings: 'Сборов',
      empty: 'За период не было ни оплат, ни проведённых сборов',
      hint: 'Стоимость площадки берётся из прайса «цена часа × длительность», а не из расчётов с площадкой',
    },
    noValue: '—',
    of: '{part} из {total}',
    target: 'норма {target}',
    prev: 'было {value}',
    cards: {
      survival: 'Дожили до {index}-го сбора',
      attendance: 'Средняя явка',
      renewal: 'Повторная покупка сезона',
      referralSeats: 'Места по рекомендации',
      venueCost: 'Площадка на человека',
    },
    hints: {
      survival: 'Доля отрядов, проведших четвёртый сбор',
      attendance: 'Пришло от состава на проведённых сборах',
      renewal: 'Отряды, продлившие сезон, из тех, кому предложили',
      referralSeats: 'Мест, занятых по приглашению участника',
      venueCost: 'Цена часа площадки, делённая на пришедших',
    },
    charts: {
      attendance: 'Явка по неделям, %',
      volume: 'Сборы и отряды по неделям',
      revenue: 'Выручка по неделям, ₽',
      venueCost: 'Площадка на человека, ₽',
      gatheringsDone: 'Сборы',
      squadsFormed: 'Отряды собраны',
      ordersPaid: 'Оплаты',
      empty: 'Данных за период нет',
    },
    funnel: {
      title: 'Воронка онбординга',
      subtitle: 'Уникальные люди на каждом шаге за период',
      steps: {
        app_opened: 'Открыли приложение',
        onboarding_started: 'Начали онбординг',
        onboarding_completed: 'Прошли онбординг',
        pool_joined: 'Встали в пул',
        member_joined: 'Попали в отряд',
        order_paid: 'Оплатили',
      },
      step: 'Шаг',
      people: 'Человек',
      ofFirst: 'от первого шага',
      ofPrev: 'от предыдущего',
      hint: 'За 100 % взят первый шаг с данными: события с клиента пишутся с сентября 2026',
    },
    survival: {
      title: 'Дожитие отрядов по сборам',
      subtitle: 'Сколько отрядов провело N сборов',
      index: 'Сбор',
      squads: 'Отрядов',
      share: 'Доля',
    },
    seats: {
      title: 'Источники занятых мест',
      source: 'Источник',
      count: 'Мест',
      share: 'Доля',
      names: {
        referral: 'По рекомендации',
        pool: 'Из пула',
        substitute: 'Замена',
        member: 'Свой человек',
        ops: 'Оператор',
      },
    },
    cells: {
      title: 'Время в пуле по ячейкам',
      subtitle: 'Медиана ожидания среди тех, кто ждёт сейчас',
      activity: 'Активность',
      size: 'Размер',
      weekday: 'Вечер',
      level: 'Уровень',
      people: 'Человек',
      median: 'Медиана, дней',
      max: 'Максимум, дней',
      empty: 'Ждущих заявок нет',
    },
    report: {
      title: 'Еженедельный отчёт владельцу',
      subtitle: 'Уходит ботом в понедельник в 9:00 по времени города',
    },
    export: {
      title: 'Выгрузка CSV',
      weeks: 'Недели',
      gatherings: 'Сборы',
      squads: 'Отряды',
      orders: 'Заказы',
      cells: 'Ячейки пула',
      done: 'Файл {name}: строк {rows}',
    },
  },
  renewal: {
    title: 'Продления сезона',
    subtitle: 'Повторная покупка: сколько отрядов и людей продлили сезон',
    offeredSeasons: 'Предложено продлений',
    renewedSeasons: 'Сезонов продлено',
    squadRate: 'Доля отрядов',
    offeredMembers: 'Участников в отрядах',
    renewedMembers: 'Оплатили продление',
    memberRate: 'Доля людей',
    pausedSquads: 'Отрядов на паузе',
    run: 'Запустить продление',
    runHint: 'Обычно продление ставит планировщик после пятого сбора',
  },
  seats: {
    title: 'Открытые места',
    subtitle:
      'Места в отрядах, замены на сбор, место запасного и разовые места: кому предложено и кто занял',
    /** Раунд 4, O13: «Живые» — по умолчанию */
    filters: { state: 'Состояние', kind: 'Вид места', live: 'Живые: в очереди и предложено' },
    states: {
      OPEN: 'В очереди',
      OFFERED: 'Предложено',
      FILLED: 'Занято',
      EXPIRED: 'Истекло',
      CANCELLED: 'Снято',
    },
    kinds: {
      SEASON: 'Место в отряде',
      RESERVE: 'Место запасного',
      SUBSTITUTE: 'Замена на сбор',
      SINGLE: 'Разовое место',
    },
    sources: {
      referral: 'По приглашению',
      pool: 'Из набора',
      substitute: 'Замена',
      member: 'По ссылке участника',
      ops: 'Оператор',
    },
    columns: {
      created: 'Открыто',
      squad: 'Отряд',
      kind: 'Вид',
      price: 'Цена',
      offered: 'Кому предложено',
      state: 'Состояние',
      filled: 'Занял',
    },
    empty: 'Открытых мест нет',
    squadLine: '{activity} · {weekday} {time} · уровень {level}',
    members: '{count} из {size} в составе',
    gathering: 'Сбор {date}',
    offerUntil: 'до {deadline}',
    offeredCount: 'предлагали {count}',
    noOffer: 'Ждёт кандидата',
    candidates: 'Кандидаты',
    candidatesTitle: 'Кому уйдёт место',
    candidatesHint:
      'Порядок очереди: сначала приглашённые кем-то из состава, потом по приоритету заявки и дате',
    candidatesEmpty: 'Кандидатов в наборе нет: место ждёт новых заявок',
    candidateLine: 'приоритет {priority}',
    referralTag: 'Друг из состава',
    offeredTag: 'Уже предлагали',
    offer: 'Предложить',
    offerDone: 'Место предложено',
    cancel: 'Снять место',
    cancelTitle: 'Снять место',
    cancelHint: 'Место исчезнет с витрины, персональное предложение аннулируется',
    reason: 'Причина',
    reasonPlaceholder: 'Почему снимаем',
    orderHeld: 'Держит заказ',
    /**
     * Stage 17, задача 5: разовое место живёт ради позиции заявки. Раунд персон 1, O17: чья заявка —
     * именем над строкой, и у снятых и истёкших мест тоже
     */
    singleHeld: 'Держит его заявка: место освободится само — по сроку брони, выписке или отмене сбора',
    singleWas: 'Было в его заявке',
    sourceBooking: 'По заявке на разовые места',
  },
  matching: {
    title: 'Матчинг',
    subtitle: 'Предложения отрядов от алгоритма сборки: подтверди состав одной кнопкой',
    run: 'Запустить подбор',
    runHint: 'Алгоритм крутится сам раз в 6 часов; кнопка запускает прогон прямо сейчас',
    runDone: 'Новых предложений: {created}, снято устаревших: {dropped}, кандидатов в пуле: {candidates}, за {ms} мс',
    empty: 'Предложений нет: запусти подбор или добавь людей в пул',
    emptyResolved: 'Разобранных предложений нет',
    filters: { city: 'Город', state: 'Состояние' },
    states: {
      PROPOSED: 'Ожидают решения',
      APPROVED: 'Утверждённые',
      REJECTED: 'Отклонённые',
    },
    stale: 'Кандидаты уже не в пуле — запусти подбор заново',
    cell: '{activity} · {weekday} {time} · размер {size} · уровень {level}',
    counts: '{count} из {size}, минимум запуска {min}',
    ages: 'Возраст {from}–{to}',
    districts: 'Районы: {names}',
    score: 'Скор {score}',
    why: 'Почему вместе',
    reasons: {
      district: 'Общий или соседний район',
      availability: 'Совпадают свободные вечера',
      level: 'Близкий уровень',
      wouldPlayAgain: 'Уже играли вместе и отметили друг друга',
      referral: 'Пришли по приглашению друг друга',
      reliability: 'Штраф за низкую надёжность',
      priority: 'Приоритет заявки',
    },
    seed: 'Вокруг него собран состав',
    member: '{name}, {age} · уровень {level} · {districts} · в наборе {days} дн.',
    memberNoAge: '{name} · уровень {level} · {districts} · в наборе {days} дн.',
    approve: 'Утвердить',
    approveTitle: 'Утвердить состав',
    approveHint:
      'Будет создан отряд, черновик сезона по шаблону и открыты продажи. Участники получат «Отряд собран» и «Открыты продажи»',
    squadName: 'Имя отряда',
    squadNamePlaceholder: 'Пусто — соберём из активности и вечера',
    template: 'Шаблон сезона',
    openSale: 'Сразу открыть продажи сезона',
    openSaleOff: 'Отряд будет создан в статусе «Собирается», сезон соберёшь в конструкторе',
    reject: 'Отклонить',
    rejectTitle: 'Отклонить предложение',
    rejectHint: 'Кандидаты останутся в пуле и попадут в следующий прогон',
    edit: 'Изменить',
    editHint: 'Открывает ручную сборку с подставленными людьми и параметрами',
    comment: 'Комментарий',
    commentPlaceholder: 'Почему отклоняем',
    openSquad: 'Открыть отряд',
    metrics: {
      title: 'Метрики пула',
      candidates: 'Кандидатов в пуле',
      cells: 'Ячеек',
      medianWait: 'Медианное ожидание, дней',
      denseShare: 'Ячеек, где набрался минимум для запуска',
      longWait: 'Ждут дольше 14 дней',
      top: 'Самые полные ячейки',
      columns: {
        cell: 'Ячейка',
        count: 'Людей',
        progress: 'Прогресс',
        wait: 'Медианное ожидание',
      },
    },
  },
  flags: {
    title: 'Флаги',
    subtitle: 'Жалобы участников, отметки капитанов, скрытые флаги из обратной связи и неявки',
    empty: 'Разобранных флагов нет',
    emptyOpen: 'Неразобранных флагов нет',
    openCount: 'Неразобранных: {count}',
    filters: {
      state: 'Состояние',
      kind: 'Вид',
      source: 'Источник',
      search: 'Участник',
      searchPlaceholder: 'Имя, username или Telegram ID',
    },
    states: { open: 'Неразобранные', resolved: 'Разобранные' },
    kind: {
      NO_SHOW: 'Неявка',
      LATE: 'Опоздание',
      TOXIC: 'Токсичное поведение',
      LEVEL_MISMATCH: 'Уровень не тот',
      OTHER: 'Другое',
    },
    source: {
      feedback: 'Обратная связь',
      captain: 'Капитан',
      member: 'Жалоба участника',
      system: 'Система',
    },
    columns: {
      created: 'Когда',
      user: 'На кого',
      kind: 'Вид',
      source: 'Источник',
      text: 'Что написано',
      context: 'Сбор и отряд',
      state: 'Состояние',
      actions: '',
    },
    reporter: 'От: {name}',
    reporterHidden: 'Автор виден только оператору',
    stats: 'Надёжность {reliability} · неявок {noShows} · открытых флагов {openFlags}',
    resolve: 'Разобрать',
    resolveTitle: 'Решение по флагу',
    resolution: 'Что делаем',
    resolutions: {
      dismiss: 'Отклонить',
      warn: 'Предупредить',
      remove: 'Исключить из отряда',
    },
    resolutionHints: {
      dismiss: 'Флаг закрывается без последствий',
      warn: 'Участник получит напоминание правил. Подтверждённый TOXIC снимает 25 баллов надёжности',
      remove: 'Участник уходит из состава, возврат считается от оставшихся сборов',
    },
    comment: 'Комментарий',
    commentPlaceholder: 'Что решили и почему',
    refundPct: 'Возврат, % от оставшихся сборов',
    refundHint: 'Оплачено {amount}, впереди {left} из {total} сборов. К возврату: {refund}',
    refundNoOrder: 'Оплаченного заказа сезона нет — возврата не будет',
    backToPool: 'Вернуть участника в пул города',
    resolvedAs: '{resolution}',
    done: 'Флаг разобран. Возврат: {refund}',
    noSquad: 'Не в отряде',
  },
  notifications: {
    title: 'Уведомления',
    subtitle: 'Журнал отправок бота, очереди задач и ручная рассылка',
    empty: 'Отправок пока нет',
    filters: {
      kind: 'Вид',
      /** Не «Тема»: так подписан переключатель оформления в шапке */
      topic: 'Тема сообщения',
      state: 'Состояние',
      search: 'Получатель',
      searchPlaceholder: 'Имя, username или Telegram ID',
    },
    columns: {
      created: 'Создано',
      recipient: 'Получатель',
      squad: 'Отряд',
      kind: 'Вид',
      text: 'Сообщение',
      state: 'Состояние',
      sentAt: 'Отправлено',
    },
    state: {
      queued: 'В очереди',
      sent: 'Отправлено',
      failed: 'Ошибка',
    },
    kind: {
      SQUAD_FORMED: 'Отряд собран',
      SALE_OPEN: 'Открыты продажи',
      PAY_DEADLINE: 'Дедлайн оплаты',
      RSVP_REQUEST: 'Идёшь?',
      DETAILS_24H: 'Детали за сутки',
      DETAILS_3H: 'Детали за 3 часа',
      FEEDBACK: 'Обратная связь',
      RENEWAL: 'Продление',
      SEAT_OFFER: 'Свободное место',
      GATHERING_CHANGED: 'Изменение сбора',
      CUSTOM: 'Сообщение',
    },
    /**
     * Раунд 4, O14: тема сообщения вида CUSTOM по ключу (`topicOf` в API). Чего нет в списке —
     * подписывается «Сообщение».
     */
    topics: {
      manual: 'Ручная рассылка',
      group: 'В группу отряда',
      'order:paid': 'Оплата получена',
      'order:paid_late': 'Заявка: оплата после брони',
      'order:refund': 'Возврат',
      'order:expired': 'Заявка: бронь истекла',
      'order:ops_cancelled': 'Заявка закрыта оператором',
      'order:item_cancelled': 'Заявка: выписка с позиции',
      'order:unfulfilled': 'Заявка: место не закрепилось',
      'order:guest_captain': 'Капитану: гость на сборе',
      'order:guest_left_captain': 'Капитану: гость выписался',
      'order:seat_taken': 'Место уже занято',
      'season:launched': 'Сезон запущен',
      'season:failed': 'Сезон не набрал оплат',
      'season:paused': 'Отряд на паузе',
      'season:renewal_seats': 'Продление: свободные места',
      'season:renewal_short': 'Продление: недобор',
      'seat:opened': 'Открыто место',
      'seat:expired': 'Место: срок вышел',
      'gathering:short': 'Недобор на сбор',
      'gathering:short_ops': 'Недобор: оператору',
      'gathering:no_show': 'Неявка',
      'gathering:move_request': 'Просьба перенести сбор',
      'tournament:assigned': 'Турнир: соперник',
      'extra:created': 'Доп. сбор по опросу',
      'ops-extra:created': 'Доп. сбор от оператора',
      'pool:longwait': 'Долго в наборе',
      'pool:transfer': 'Переход в другой набор',
      'venue:booking': 'Бронь площадке',
      'venue:declined': 'Площадка отказала',
      'venue:restored': 'Площадка вернула бронь',
      'venue:reminder': 'Напоминание площадке',
      'flag:resolved': 'Решение по жалобе',
      'proposal:resolved': 'Решение по активности',
      'referral:earned': 'Бонус за друга',
      'reserve:accepted': 'Запасной принял приглашение',
      level: 'Уровень уточнён',
      digest: 'Сводка капитану',
      report: 'Отчёт по городу',
      'queue-alert': 'Сбой очереди',
    } satisfies Record<NotificationTopic, string> as Record<string, string>,
    /**
     * Группы фильтра «Тема» (O1): заявки и оплаты — первыми, их ищут чаще всего. Каждая тема ровно в
     * одной группе — `texts.test.ts`.
     */
    topicGroups: [
      {
        label: 'Заявки и оплаты',
        topics: [
          'order:paid',
          'order:paid_late',
          'order:refund',
          'order:expired',
          'order:ops_cancelled',
          'order:item_cancelled',
          'order:unfulfilled',
          'order:seat_taken',
          'order:guest_captain',
          'order:guest_left_captain',
          'referral:earned',
        ],
      },
      {
        label: 'Сезоны и сборы',
        topics: [
          'season:launched',
          'season:failed',
          'season:paused',
          'season:renewal_seats',
          'season:renewal_short',
          'gathering:short',
          'gathering:short_ops',
          'gathering:no_show',
          'gathering:move_request',
          'extra:created',
          'ops-extra:created',
          'tournament:assigned',
        ],
      },
      {
        label: 'Места и набор',
        topics: [
          'seat:opened',
          'seat:expired',
          'pool:longwait',
          'pool:transfer',
          'reserve:accepted',
          'level',
        ],
      },
      {
        label: 'Площадки',
        topics: ['venue:booking', 'venue:declined', 'venue:restored', 'venue:reminder'],
      },
      {
        label: 'Прочее',
        topics: [
          'manual',
          'group',
          'flag:resolved',
          'proposal:resolved',
          'digest',
          'report',
          'queue-alert',
        ],
      },
    ] satisfies { label: string; topics: NotificationTopic[] }[],
    squadMore: '+{count}',
    /**
     * Причины неудачной отправки словами. Ключи — коды из `api/src/bot/notify.ts`; всё, чего
     * здесь нет, это текст ошибки Telegram, он показывается как есть.
     */
    errorReason: {
      bot_off: 'Бот не запущен получателем',
      blocked: 'Получатель заблокировал бота',
      muted: 'Выключено настройками получателя',
      not_found: 'Получатель не найден в Telegram',
      no_chat: 'Нет чата с получателем',
      empty_text: 'Пустой текст сообщения',
    } as Record<string, string>,
    group: 'Группа отряда',
    resend: 'Отправить заново',
    queues: 'Очереди',
    /** Раунд 3, O7: имена очередей BullMQ словами (`apps/api/src/jobs/queue.ts`) */
    queueNames: {
      season: 'Сезоны и сроки оплаты',
      gathering: 'Напоминания о сборах',
      notify: 'Сообщения в бот',
      digest: 'Сводки',
      matching: 'Подбор отрядов',
      seat: 'Открытые места',
      poll: 'Опросы',
    },
    /** Раунд 4, O3: «выключены» — только когда сервер так ответил, а не пока грузится или при ошибке */
    queuesEmpty: 'Очереди в этом процессе API выключены: нет Redis или JOBS_ENABLED=false',
    queuesLoading: 'Считаем очереди…',
    queuesError: 'Не удалось получить очереди: {error}. Это не значит, что они выключены',
    queuesNone: 'Очереди включены, счётчиков пока нет',
    queueCounts: 'ждут {waiting} · в работе {active} · отложено {delayed} · упало {failed}',
    send: 'Написать',
    sendTitle: 'Ручная рассылка',
    sendTarget: 'Кому',
    targets: {
      squad: 'Составу отряда — в личку',
      squadChat: 'В группу отряда',
      user: 'Одному участнику',
    },
    sendSquad: 'Отряд',
    sendUser: 'Участник (id)',
    sendText: 'Текст',
    sendTextPlaceholder: 'Коротко и по делу: что изменилось и что сделать',
    sendResult: 'Отправлено {sent}, в очереди {queued}, пропущено {skipped}, с ошибкой {failed}',
    sendNoTarget: 'Выбери получателя',
    sendNoText: 'Напиши текст',
    chooseSquad: 'Выбери отряд',
  },
  orders: {
    title: 'Заказы',
    /** Раунд персон 1, O16: заявки на разовые места живут здесь же */
    subtitle:
      'Оплаты сезонов и заявки на разовые места: ручное подтверждение переводов, возвраты, «ждут возврата»',
    /** Раунд персон 1, O14: кнопка «не делать» в окне закрытия заказа или заявки */
    dismiss: 'Отмена',
    empty: 'Заказов пока нет',
    emptyRefundPending: 'Возвращать нечего: ни один заказ не ждёт возврата',
    filters: {
      status: 'Статус',
      provider: 'Провайдер',
      search: 'Плательщик',
      searchPlaceholder: 'Имя, username или Telegram ID',
      /** Stage 17, задача 6: пометка у заказа или у любой позиции заявки */
      refundPending: 'Только ждут возврата',
    },
    columns: {
      created: 'Создан',
      user: 'Плательщик',
      squad: 'Отряд · сезон',
      kind: 'Тип',
      amount: 'Сумма',
      provider: 'Провайдер',
      status: 'Статус',
      paidAt: 'Оплачен',
      actions: 'Действия',
    },
    /** Раунд персон 2, O4: при ширине меньше 1280 дата оплаты — под статусом */
    paidAtLine: 'оплачен {at}',
    /** Раунд персон 2, O8: срок живой брони в строке заявки */
    holdUntil: 'бронь до {time}',
    /** Раунд персон 2, O4: второстепенные действия строки — в меню «…» */
    menu: '{user}, {amount}: ещё действия',
    /** Раунд персон 2, O9: окно закрытия называет счёт, сумму, плательщика и что он получит */
    cancelTitle: 'Закрыть заказ?',
    cancelWhat: 'Счёт № {n} · {amount} · {user}',
    cancelNotifyNone: 'Сообщения участнику не будет — если нужно, напиши ему сам',
    cancelNotifyDeleted: 'Аккаунт участника удалён — сообщение не уйдёт',
    /** Раунд персон 2, O10: кнопка окна говорит, что делает, — деньги двигаются не здесь */
    refundSubmit: 'Записать возврат',
    refundDeletedHint:
      'Аккаунт участника удалён: сообщение о возврате не уйдёт. Робокасса вернёт деньги на карту, с которой платили; перевод — только по реквизитам из прежней переписки. Порядок — runbook «Возврат», раздел «Удалённый участник»',
    kind: {
      SEASON: 'Сезон',
      TRIAL: 'Пробный сбор',
      SUBSTITUTE: 'Замена',
      EXTRA: 'Доп. сбор',
      CORPORATE: 'Корпоративный',
      BUNDLE: 'Заявка',
      SINGLE: 'Разовое место',
    },
    provider: {
      MANUAL: 'Перевод (вручную)',
      TELEGRAM: 'Счёт Telegram',
      ROBOKASSA: 'Робокасса',
      STARS: 'Stars',
    },
    status: {
      PENDING: 'Ждёт оплаты',
      PAID: 'Оплачен',
      REFUNDED: 'Возвращён',
      PARTIALLY_REFUNDED: 'Частичный возврат',
      CANCELLED: 'Отменён',
      FAILED: 'Не прошёл',
    },
    discount: 'скидка {amount}',
    refunded: 'возвращено {amount}',
    refundPending: 'Возврат через провайдера не прошёл — верни вручную и запиши как внешний',
    markPaid: 'Подтвердить оплату',
    markPaidPrompt: 'Как пришли деньги (СБП, карта, дата) — попадёт в журнал',
    markPaidProvider: 'Подтвердить вручную',
    markPaidProviderPrompt:
      'Заказ провайдера. Подтверждайте, только если видите деньги в кабинете провайдера: напишите, где именно (кабинет, дата, сумма) — попадёт в журнал',
    /** Раунд 4, O9: окно вместо системного prompt */
    markPaidBody: '{amount} от {user} · {provider}. Место в составе закрепится, участник получит сообщение',
    markPaidComment: 'Как пришли деньги',
    markPaidCommentProvider: 'Где видны деньги',
    markPaidCommentRequired: 'Для заказа провайдера напиши, где видны деньги: кабинет, дата, сумма',
    /** Раунд 4, O8: машинные причины закрытия заказа — словами */
    reasonCodes: {
      account_deleted: 'Аккаунт удалён — счёт закрыт',
      hold_expired: 'Бронь не оплачена в срок — заявка снята',
      booking_replaced: 'Участник оформил другую заявку — эта снята',
      ops_cancelled: 'Закрыта оператором',
      item_cancelled: 'Выписка до порога отмены — полный возврат',
      item_unfulfilled: 'Оплачена, но место не вручено',
      late_payment: 'Деньги пришли после снятия брони',
      gathering_cancelled: 'Сбор отменён или перенесён',
    },
    /** Stage 17, задача 6: заявка `BUNDLE` одной строкой с раскрываемыми позициями */
    bundle: {
      invoice: 'Счёт № {n}',
      showItems: 'Позиции: {n}',
      item: 'Позиция',
      gathering: 'Сбор {index} · {when}',
      /** Раунд персон 2, O3: доп. сбор без внутреннего номера */
      gatheringExtra: 'Доп. сбор · {when}',
      gatheringCancelled: 'сбор отменён',
      /**
       * Раунд персон 2, O11: человек выписался, а сбор потом отменили — причина возврата остаётся
       * «выписка» (её сумма и правило), отмена сбора — только факт о сборе
       */
      gatheringCancelledAfter: 'сбор отменён уже после выписки',
      gatheringCancelledLater: 'сбор отменён позже — причина возврата прежняя',
      itemsDue: 'Ждут возврата позиций: {n}',
      itemDue: 'Ждёт возврата',
      /** Сколько вернуть — runbook «Возврат позиции заявки» */
      dueRule: {
        item_cancelled: 'вернуть сумму позиции целиком',
        item_unfulfilled: 'вернуть сумму позиции целиком',
        late_payment: 'вернуть всю заявку — «Возврат» на строке заявки',
        gathering_cancelled: 'вернуть сумму позиции целиком',
      },
      unfulfilledCause: {
        gathering_closed: 'сбор закрыт или уже начался',
        seat_taken: 'место ушло другому',
        already_going: 'участник уже в составе',
      },
      credited: 'Зачтена в сезон — без решения владельца не возвращать',
      /** Раунд персон 1, O14: заявка закрывается как заявка, а не «заказ» */
      cancel: 'Закрыть заявку',
      cancelTitle: 'Закрыть заявку?',
      cancelNotify:
        'Участник получит сообщение в боте: заявка закрыта оператором, со списком позиций',
      /** Раунд персон 1, O12: окно возврата позиции называет позицию и что делать */
      refundItemTitle: 'Возврат по позиции заявки',
      refundItemWhat: 'Позиция: {kind} · {gathering} · {squad}',
      refundAdvice: 'Рекомендация: {rule}',
      refundAdviceNone:
        'Рекомендация: позиция не ждёт возврата — возвращай только по просьбе участника или решению владельца',
      cancelConfirm:
        'Закрыть заявку без оплаты? Все позиции закроются, места вернутся на витрину. Если деньги всё же придут, заявка встанет в «ждут возврата»',
      markPaidBody:
        '{amount} от {user} · {provider}. Места по позициям ({n}) закрепятся за участником',
      refundItemHint:
        'Позиция заявки, счёт № {invoice}: верни сумму позиции (в кабинете Робокассы — частичным возвратом по этому счёту, при переводе — переводом) и зафиксируй здесь',
      refundBundleHint:
        'Возврат по заявке разложится по позициям: сначала те, что ждут возврата. Для одной позиции нажми «Возврат» на её строке',
    },
    cancel: 'Закрыть заказ',
    cancelConfirm: 'Закрыть заказ без оплаты? Участник сможет создать новый',
    refund: 'Возврат',
    refundTitle: 'Возврат по заказу',
    refundBody: 'К возврату доступно {remaining} — {user}',
    refundAmount: 'Сумма, ₽',
    refundAmountHint: 'Пусто — вернуть весь остаток',
    refundReason: 'Причина (увидит участник)',
    refundReasonRequired: 'Укажи причину возврата',
    refundBadAmount: 'Некорректная сумма',
    refundExternal: 'Деньги уже вернули вне системы (не вызывать API провайдера)',
    refundManualHint: 'Ручной заказ: перевод возвращаешь сам, здесь только фиксируем',
    refundCabinetHint: 'Возврат делается в личном кабинете Робокассы, здесь только фиксируем',
    payload: 'Данные',
    payloadTitle: 'Данные провайдера',
    orderId: 'Заказ',
    providerPaymentId: 'Платёж',
    invoiceUrl: 'Счёт',
  },
  settings: {
    title: 'Настройки',
    subtitle: 'Цены, параметры и шаблоны сезонов. Менять может только владелец',
    readOnly: 'Менять настройки может только владелец',
    groups: {
      pricing: 'Цены',
      squad: 'Отряд и пул',
      activity: 'Предложенные активности',
      feature: 'Фиче-флаги',
      payment: 'Платежи',
      booking: 'Заявки и разовые места',
    },
    keys: {
      'pricing.seasonDefault': 'Сезон из 6 сборов',
      'pricing.trial': 'Пробный сбор',
      'pricing.substitute': 'Замена на один сбор',
      'pricing.renewalDiscount': 'Скидка продления',
      'pricing.referralBonus': 'Реферальный бонус обоим',
      'pricing.captainDiscount': 'Скидка капитану',
      'squad.saleWindowDays': 'Дедлайн гарантии набора, дней',
      'squad.minToLaunchRatio': 'Минимум для запуска, доля размера',
      'squad.defaultCapacity': 'Размер отряда для неосновных активностей',
      'squad.reserveInviteTtlDays': 'Срок приглашения запасного, дней',
      'squad.maxPoolApplications': 'Заявок в пуле на человека',
      'activity.proposalSizeMin': 'Минимальный размер',
      'activity.proposalSizeMax': 'Максимальный размер',
      'feature.openSeats': 'Открытые места в приложении (экран набора)',
      'feature.trial': 'Пробный сбор в приложении (экран набора)',
      'payment.deadlineExtensions': 'Сколько раз можно продлить дедлайн оплаты',
      'feature.myGatherings': '«Мои сборы» и заявка на несколько разовых мест',
      'feature.myGatheringsCities': '«Мои сборы»: города выкатки (пусто — все города)',
      'pricing.singleRatio': 'Разовое место: множитель к доле сезона',
      'pricing.singleCreditDays': 'Зачёт разовых мест в сезон, дней',
      'booking.holdMinutes': 'Бронь заявки до оплаты, минут',
      'booking.maxItems': 'Сборов в одной заявке',
      'booking.noSeasonMax': 'Разовых записей без сезона в окне',
      'booking.noSeasonWindowDays': 'Окно лимита без сезона, дней',
      'booking.singleShareMax': 'Доля мест сбора под разовые и пробные',
      'booking.singleSaleDays': 'Место на сезон отдаём разовым за столько дней до сбора',
      'booking.cancelFullRefundHours': 'Отмена позиции с полным возвратом, часов до начала',
    },
    columns: {
      key: 'Ключ',
      value: 'Значение',
      default: 'По умолчанию',
      updatedAt: 'Изменено',
    },
    defaultValue: 'дефолт',
    /** Список городов выкатки флага пуст (ADR-47) */
    allCities: 'все города',
    /** Раунд персон 1, O19: города выкатки — отметками из списка, а не slug-ами через запятую */
    citiesHint: 'Ничего не отмечено — все города',
    citiesLabel: 'Города выкатки',
    templates: 'Шаблоны сезона',
    templatesEmpty: 'Шаблонов нет',
    templateAdd: 'Новый шаблон',
    templateTitle: 'Шаблон сезона',
    templateCode: 'Код (латиница)',
    templateName: 'Название',
    templateSlots: 'Слоты сборов',
    templateSlotsHint: 'sport — основная активность отряда; остальные — код неосновной активности',
    templateSeasonal: 'Сезонность',
    seasonalNone: 'круглый год',
    seasonalWinter: 'зима',
    seasonalSummer: 'лето',
    templateDelete: 'Удалить',
    templateDeleteConfirm: 'Удалить шаблон «{name}»?',
    sport: 'основная активность',
  },
  venues: {
    title: 'Площадки',
    subtitle: 'Координаты, активности, цена часа, непиковые окна',
    add: 'Добавить площадку',
    empty: 'Площадок нет',
    showInactive: 'Показывать неактивные',
    columns: {
      name: 'Площадка',
      city: 'Город',
      address: 'Адрес',
      activities: 'Активности',
      price: 'Цена часа',
      capacity: 'Вместимость',
      offPeak: 'Непик',
      partner: 'Партнёр',
      active: 'Активна',
    },
    /** Доступ сотрудников площадки к порталу (Stage 14, задача 2). */
    access: {
      button: 'Доступ',
      title: 'Доступ к порталу площадки',
      hint: 'Сотрудник должен один раз открыть бота — тогда его Telegram ID найдётся',
      tgId: 'Telegram ID сотрудника',
      add: 'Дать доступ',
      empty: 'Доступ никому не выдан',
      name: 'Сотрудник',
      since: 'С',
      revoke: 'Отозвать',
      revoked: 'Отозван',
    },
    form: {
      createTitle: 'Новая площадка',
      editTitle: 'Площадка',
      name: 'Название',
      address: 'Адрес',
      coordinates: 'Координаты',
      paste: 'Ссылка Яндекс.Карт или «широта, долгота»',
      parse: 'Разобрать',
      parseFailed: 'Координаты не найдены. Вставь ссылку с ?ll= или «55.75, 37.62»',
      lat: 'Широта',
      lng: 'Долгота',
      activities: 'Активности',
      noActivitiesWarning:
        'Не отмечено ни одной активности — конструктор сезона не предложит эту площадку ни для одного сбора',
      contact: 'Контакт',
      price: 'Цена часа, ₽',
      capacity: 'Вместимость (человек за слот)',
      offPeak: 'Непиковые окна',
      offPeakAdd: 'Добавить окно',
      offPeakDiscount: 'Скидка, %',
      notes: 'Заметки',
      isPartner: 'Партнёрская площадка',
      isActive: 'Активна',
      mapLink: 'Открыть на карте',
    },
  },
  activities: {
    title: 'Активности',
    subtitle: 'Каталог: размеры отряда, минимум для сбора, длительность, цена сезона, города',
    add: 'Добавить активность',
    empty: 'Активностей нет',
    allCities: 'все города',
    columns: {
      name: 'Активность',
      code: 'Код',
      primary: 'Основная',
      sizes: 'Размеры',
      minPlay: 'Мин. на сбор',
      duration: 'Длительность',
      level: 'Уровень',
      cities: 'Города',
      price: 'Цена сезона',
      venues: 'Площадки',
      waiting: 'В пуле',
      source: 'Источник',
      active: 'Активна',
    },
    detail: {
      title: 'Активность',
      notFound: 'Активность не найдена',
      venues: 'Площадки',
      venuesEmpty: 'Площадок с этой активностью нет',
      venuePending: 'Площадку ищем',
      proposedBy: 'Предложил',
    },
    form: {
      createTitle: 'Новая активность',
      code: 'Код (латиница)',
      name: 'Название',
      description: 'Описание',
      isPrimary: 'Основная (можно собрать отряд)',
      sizeOptions: 'Размеры отряда через запятую',
      minPlay: 'Минимум людей на сбор',
      duration: 'Длительность сбора, мин',
      indoor: 'В помещении',
      needsLevel: 'Нужен уровень',
      isActive: 'Активна',
      cityIds: 'Города (пусто — во всех)',
      seasonPrice: 'Цена сезона, ₽ (пусто — по умолчанию)',
      reserveSlots: 'Мест для запасных',
      reservePriceRatio: 'Доля цены для запасного',
      rotationHint: 'Подсказка о ротации',
      venuePending: 'Площадку ищем',
      sizesInvalid: 'Размеры: целые числа от 2 до 16',
    },
  },
  proposals: {
    title: 'Предложения',
    subtitle: 'Активности от пользователей: подтвердить, объединить или отклонить',
    empty: 'Предложений нет',
    statusFilter: 'Статус',
    sizes: 'Нужно людей',
    needsLevel: 'нужен уровень',
    venueHint: 'Где проходит',
    author: 'Автор',
    authorNotOnboarded: 'без онбординга',
    result: 'Результат',
    approve: 'Подтвердить',
    merge: 'Объединить с…',
    reject: 'Отклонить',
    approveTitle: 'Подтвердить активность',
    approveBody: 'Активность появится у всех в городе {city}; автор попадёт в набор первым',
    venues: 'Площадки города',
    venuesEmpty: 'В городе нет площадок — отметь «площадку ищем» или заведи площадку',
    venuePending: 'Площадку ищем',
    authorSize: 'Размер отряда для заявки автора',
    authorSizeAuto: 'первый из списка',
    mergeTitle: 'Объединить с существующей активностью',
    mergeActivity: 'Активность',
    rejectTitle: 'Отклонить предложение',
    rejectComment: 'Причина для автора',
    decided: 'Решение принято: {status}',
    authorApplication: {
      created: 'заявка автора создана',
      exists: 'у автора уже есть заявка на эту активность',
      limit: 'у автора исчерпан лимит заявок',
      not_onboarded: 'автор ещё не прошёл онбординг — заявка появится после него',
      in_squad: 'автор уже в отряде по этой активности',
    },
  },
  weekdaysShort: ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'],
  weekdaysLong: ['понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота', 'воскресенье'],
  ageBands: ['18–26', '27–32', '33–38', '39–45', '46+'],
  levels: ['1 · новичок', '2 · начинающий', '3 · средний', '4 · уверенный', '5 · сильный'],
  /** Сообщения валидации zod по-русски (раунд 3, O7), `lib/zodRu.ts` */
  zod: {
    required: 'Заполни поле',
    invalid: 'Проверь значение',
    number: 'Нужно число',
    integer: 'Нужно целое число',
    text: 'Нужен текст',
    minLength: 'Не короче {n} символов',
    maxLength: 'Не длиннее {n} символов',
    min: 'Не меньше {n}',
    max: 'Не больше {n}',
    greater: 'Больше {n}',
    less: 'Меньше {n}',
    pickOne: 'Выбери хотя бы один вариант',
    minItems: 'Нужно не меньше {n}',
    maxItems: 'Не больше {n} в списке',
    email: 'Нужен адрес почты',
    url: 'Нужна ссылка, начиная с https://',
    date: 'Проверь дату',
    format: 'Неверный формат',
    choose: 'Выбери значение из списка',
    multipleOf: 'Число должно делиться на {n}',
    withField: '{field}: {text}',
    fields: {
      name: 'Название',
      code: 'Код',
      slug: 'Код',
      address: 'Адрес',
      capacity: 'Вместимость',
      pricePerHourKopecks: 'Цена часа',
      priceKopecks: 'Цена',
      seasonPriceKopecks: 'Цена сезона',
      sizeOptions: 'Размеры',
      minPlay: 'Минимум для игры',
      durationMin: 'Длительность',
      discountPct: 'Скидка',
      from: 'Начало',
      to: 'Конец',
      weekday: 'День',
      startTime: 'Время',
      startsAt: 'Начало',
      startsOn: 'Дата начала',
      timezone: 'Часовой пояс',
      tgId: 'Telegram ID',
      contact: 'Контакт',
      comment: 'Комментарий',
      reason: 'Причина',
      opsComment: 'Комментарий',
      districts: 'Районы',
      districtIds: 'Районы',
      activityIds: 'Активности',
      lat: 'Широта',
      lng: 'Долгота',
      promoCode: 'Промокод',
      value: 'Значение',
      slots: 'Сборы',
      text: 'Текст',
    },
  },
  /** Журнал действий словами (раунд 3, O7): коды — `apps/api/src/lib/audit.ts` */
  auditAction: {
    'squad.create': 'Отряд создан',
    'squad.update': 'Отряд изменён',
    'squad.addMember': 'Участник добавлен',
    'squad.removeMember': 'Участник убран',
    'squad.setCaptain': 'Назначен капитан',
    'squad.bindChat': 'Привязана группа',
    'squad.openSale': 'Открыты продажи',
    'squad.launch': 'Отряд запущен',
    'squad.disband': 'Отряд распущен',
    'squad.setName': 'Название отряда',
    'squad.setEmblem': 'Эмблема отряда',
    'squad.pause': 'Отряд на паузе',
    'venue.create': 'Площадка заведена',
    'venue.update': 'Площадка изменена',
    'venue.bookingDecide': 'Решение площадки по брони',
    'venue.selfUpdate': 'Площадка изменила свои данные',
    'venueUser.add': 'Сотрудник площадки добавлен',
    'venueUser.remove': 'Сотрудник площадки убран',
    'activity.create': 'Активность заведена',
    'activity.update': 'Активность изменена',
    'proposal.approve': 'Предложение принято',
    'proposal.merge': 'Предложение объединено',
    'proposal.reject': 'Предложение отклонено',
    'user.setRole': 'Роль изменена',
    'user.setStatus': 'Статус аккаунта изменён',
    'user.delete': 'Аккаунт удалён',
    'user.setLevel': 'Уровень изменён',
    'user.setNotes': 'Заметки оператора',
    'season.create': 'Сезон создан',
    'season.openSale': 'Продажи сезона открыты',
    'season.launch': 'Сезон запущен',
    'season.finish': 'Сезон завершён',
    'season.cancel': 'Сезон отменён',
    'season.extendDeadline': 'Срок оплаты продлён',
    'season.fail': 'Гарантия не набрана',
    'season.renewalOffer': 'Предложено продление',
    'season.renew': 'Сезон продлён',
    'seasonTemplate.upsert': 'Шаблон сезона сохранён',
    'seasonTemplate.delete': 'Шаблон сезона удалён',
    'gathering.update': 'Сбор изменён',
    'gathering.reschedule': 'Сбор перенесён',
    'gathering.cancel': 'Сбор отменён',
    'gathering.confirm': 'Сбор подтверждён',
    'gathering.complete': 'Сбор завершён',
    'gathering.add': 'Доп. сбор добавлен',
    'gathering.checkin': 'Отмечена явка',
    'gathering.result': 'Записан результат',
    'gathering.singlesClosed': 'Разовые места на сборе (капитан)',
    'setting.set': 'Настройка изменена',
    'order.markPaid': 'Оплата отмечена вручную',
    'order.refund': 'Возврат',
    'order.cancel': 'Заказ отменён',
    'order.refundDue': 'Позиция заявки — к возврату',
    'notify.send': 'Уведомление отправлено',
    'notify.resend': 'Уведомление отправлено повторно',
    'reliability.recalc': 'Надёжность пересчитана',
    'flag.resolve': 'Флаг разобран',
    'matching.approve': 'Предложение подбора принято',
    'matching.reject': 'Предложение подбора отклонено',
    'seat.open': 'Место открыто',
    'seat.offer': 'Место предложено',
    'seat.cancel': 'Место закрыто',
    'seat.fill': 'Место занято',
    'tournament.assign': 'Назначен соперник',
    'tournament.clear': 'Соперник снят',
    'tournament.setFormat': 'Формат турнира',
    'city.create': 'Город заведён',
    'city.update': 'Город изменён',
    'city.open': 'Набор в городе открыт',
    'city.close': 'Набор в городе закрыт',
    'city.operatorSet': 'Назначен оператор города',
    'company.create': 'Компания заведена',
    'company.update': 'Компания изменена',
    'company.invoice': 'Выставлен счёт',
  },
  squadStatus: {
    FORMING: 'Собирается',
    SALE: 'Продажи',
    ACTIVE: 'Активен',
    PAUSED: 'Пауза',
    DISBANDED: 'Распущен',
  },
  seasonStatus: {
    DRAFT: 'Черновик',
    SALE: 'Продажи',
    ACTIVE: 'Идёт',
    FINISHED: 'Завершён',
    CANCELLED: 'Отменён',
  },
  gatheringStatus: {
    PLANNED: 'Запланирован',
    CONFIRMED: 'Подтверждён',
    DONE: 'Состоялся',
    CANCELLED: 'Отменён',
  },
  poolStatus: {
    WAITING: 'Ждёт',
    OFFERED: 'Предложен',
    PLACED: 'В отряде',
    WITHDRAWN: 'Снята',
  },
  proposalStatus: {
    PENDING: 'На рассмотрении',
    APPROVED: 'Подтверждено',
    MERGED: 'Объединено',
    REJECTED: 'Отклонено',
  },
  memberStatus: {
    ACTIVE: 'В составе',
    LEFT: 'Вышел',
    REMOVED: 'Убран',
    SUBSTITUTE: 'Замена',
  },
  userStatus: {
    ACTIVE: 'Активен',
    BANNED: 'Заблокирован',
    DELETED: 'Удалён',
  },
  composition: {
    MEN: 'Мужской',
    MIXED: 'Смешанный',
  },
  /** Раунд 4, O16: поля «Подробностей» журнала словами (`lib/auditFields.ts`) */
  auditFields: {
    status: 'Статус',
    venueStatus: 'Бронь площадки',
    isReserve: 'Запасной',
    priceKopecks: 'Цена',
    amountKopecks: 'Сумма',
    refundedKopecks: 'Возвращено',
    refundKopecks: 'К возврату',
    pricePerHourKopecks: 'Цена часа',
    comment: 'Комментарий',
    reason: 'Причина',
    note: 'Причина',
    notes: 'Заметки',
    saleDeadline: 'Срок оплаты',
    deadline: 'Срок',
    via: 'Кем или чем',
    kind: 'Вид',
    minToLaunch: 'Минимум для запуска',
    minToLaunchOverridden: 'Минимум задан вручную',
    returnToPool: 'Вернуть в пул',
    backToPool: 'Вернуть в пул',
    name: 'Название',
    at: 'Когда',
    members: 'В составе',
    memberUserIds: 'Участников',
    capacity: 'Размер',
    reserveCapacity: 'Мест для запасных',
    emblem: 'Эмблема',
    tgInviteLink: 'Ссылка-приглашение',
    tgChatId: 'Группа Telegram',
    ageMin: 'Возраст от',
    ageMax: 'Возраст до',
    levelBand: 'Уровень',
    startTime: 'Время',
    weekday: 'Вечер',
    districtIds: 'Районов',
    districts: 'Районов',
    composition: 'Состав',
    templateCode: 'Шаблон',
    gatherings: 'Сборов',
    gatheringsCancelled: 'Сборов отменено',
    gatheringsDone: 'Сборов проведено',
    number: 'Номер',
    paid: 'Оплатили',
    startsOn: 'Начало',
    endsOn: 'Конец',
    fromPreview: 'Из правленого превью',
    late: 'Опоздал',
    mark: 'Отметка',
    present: 'Пришёл',
    provider: 'Способ оплаты',
    external: 'Возврат вне системы',
    contact: 'Контакт',
    isActive: 'Активна',
    offPeak: 'Непиковых окон',
    activityIds: 'Активностей',
    isPartner: 'Партнёр',
    discountPct: 'Скидка, %',
    timezone: 'Часовой пояс',
    address: 'Адрес',
    minAttendance: 'Мин. явка',
    durationMin: 'Длительность, мин',
    openedAt: 'Набор открыт',
    opponentTitle: 'Соперник',
    theirs: 'Счёт соперника',
    ours: 'Счёт отряда',
    slug: 'Код',
    inn: 'ИНН',
    mixedSquads: 'Смешанные отряды',
    manual: 'Вручную',
    openSale: 'Открыть продажи',
    votes: 'Голосов',
    tgId: 'Telegram ID',
    refundPct: 'Возврат, %',
    promoCode: 'Промокод',
    seats: 'Мест',
    seatsPurchased: 'Мест куплено',
    resolution: 'Решение',
    source: 'Источник',
    role: 'Роль',
    // Stage 17, задача 5: разовый сбор ops, формат турнира, позиция «к возврату»
    index: 'Номер сбора',
    startsAt: 'Начало',
    tournamentFormat: 'Формат турнира',
    twinFormat: 'Формат у соперника',
    refundReason: 'Причина закрытия',
  } as Record<string, string>,
  /**
   * Раунд персон 2, O3: подписи полей, которые у действия значат своё. У «Доп. сбор добавлен»
   * номер сбора — внутренний (7 после шести), вместимость — сбора, а не отряда
   */
  auditFieldsByAction: {
    'gathering.add': { index: 'Сбор', capacity: 'Вместимость сбора' },
  } as Record<string, Record<string, string>>,
  /** Значения полей по действию: вместо внутреннего номера доп. сбора — «доп.» */
  auditValuesByAction: {
    'gathering.add': { index: 'доп.' },
  } as Record<string, Record<string, string>>,
  /** Значения-коды в журнале: `via`, `kind`, `mark`, `resolution` */
  auditValues: {
    ops: 'оператор',
    deadline: 'дедлайн оплаты',
    season: 'сезон',
    captain: 'капитан',
    disband: 'роспуск отряда',
    SEASON: 'сезон',
    RESERVE: 'место запасного',
    TRIAL: 'пробный сбор',
    SUBSTITUTE: 'замена',
    absent: 'не пришёл',
    late: 'опоздал',
    present: 'пришёл',
    clear: 'снята',
    warn: 'предупреждение',
    remove: 'закрыть место',
    dismiss: 'отклонить',
    MANUAL: 'перевод',
    ROBOKASSA: 'Робокасса',
    TELEGRAM: 'счёт Telegram',
    MEN: 'мужской',
    MIXED: 'смешанный',
    STANDARD: 'обычный, до конца',
    KNOCKOUT: 'на выбывание',
    gathering_cancelled: 'сбор отменён — к возврату',
  } as Record<string, string>,
  source: {
    CATALOG: 'Каталог',
    PROPOSED: 'Предложена',
  },
  errors: {
    generic: 'Не получилось. Попробуй ещё раз',
    squad_full: 'Все места заняты',
    reserve_full: 'Место запасного занято',
    already_member: 'Уже в составе',
    already_in_squad: 'Уже в другом отряде по этой активности',
    not_member: 'Не в составе отряда',
    not_enough_members: 'В составе меньше минимума для запуска',
    comment_required: 'Нужен комментарий: откуда известно, что деньги пришли',
    unauthorized: 'Сессия истекла — войдите заново',
    account_blocked: 'Аккаунт заблокирован',
    need_role: 'Нужна роль оператора или владельца',
    /** Раунд 5, O5: оператор упёрся в действие владельца */
    owner_only: 'Это может только владелец',
    /** Раунд 5, O4: одно живое предложение на кандидата в отряде */
    candidate_holds_offer:
      'Этому человеку уже предложено другое место этого отряда — дождись ответа или сними то предложение',
    season_draft: 'Сезон ещё черновик: сборы подтверждаются после открытия продаж',
    /** Завершение сбора (раунд 5, O1) и разовый сбор ops (Stage 17, задача 5) */
    season_not_active: 'Действие доступно только в идущем сезоне',
    gathering_not_started: 'Сбор ещё не начался',
    need_venue_role: 'Раздел площадки — нужен аккаунт площадки',
    venue_account: 'Аккаунт площадки: разделы участника ему недоступны',
    squad_required: 'Не указан отряд или сбор',
    bad_status: 'Действие недоступно в текущем статусе — обновите страницу',
    squad_disbanded: 'Отряд распущен',
    season_schedule_mismatch:
      'Сборы сезона стоят на прежнем вечере. Перенеси их в конструкторе сезона или отметь «Только отряд»',
    squad_not_found: 'Отряд не найден',
    user_not_found: 'Пользователь не найден',
    user_not_active: 'Пользователь заблокирован',
    invalid_squad_size: 'Такого размера нет у активности',
    activity_not_primary: 'Эту активность нельзя выбрать основной',
    activity_not_found: 'Активность недоступна',
    district_not_in_city: 'Район не относится к городу',
    min_to_launch_too_big: 'Минимум запуска больше размера отряда',
    bad_age_range: 'Возраст «от» больше «до»',
    code_taken: 'Код активности занят',
    sizes_required: 'У основной активности нужен размер отряда',
    venue_not_found: 'Площадка не найдена в городе предложения',
    already_resolved: 'Предложение уже рассмотрено',
    proposal_not_found: 'Предложение не найдено',
    self_demote: 'Нельзя понизить себя',
    self_ban: 'Нельзя заблокировать себя',
    city_not_found: 'Город не найден',
    empty_patch: 'Нечего менять',
    season_not_found: 'Сезон не найден',
    season_exists: 'У отряда уже есть открытый сезон',
    squad_not_open: 'Отряд распущен или на паузе',
    template_not_found: 'Шаблон сезона не найден',
    template_invalid: 'Шаблон сезона повреждён',
    template_in_use: 'Шаблон уже использован в сезонах',
    gathering_not_found: 'Сбор не найден',
    opponent_is_self: 'Соперник — другой отряд',
    opponent_not_found: 'Отряд-соперник не найден в городе',
    no_gatherings: 'В сезоне нет сборов',
    gathering_in_past: 'Сбор в прошлом — поставь дату и время впереди',
    /** Раунд 4, O13 */
    recipient_gone: 'Получатель удалил аккаунт — отправлять некому',
    gatherings_overlap: 'Два сбора сезона в одно время — разведи их по датам или времени',
    bad_setting_value: 'Недопустимое значение',
    not_enough_paid: 'Оплатили меньше минимума для запуска',
    order_not_found: 'Заказ не найден',
    refund_too_big: 'Сумма возврата больше остатка по заказу',
    refund_not_configured: 'Возврат через API провайдера не настроен: отметь возврат как внешний',
    refund_rejected: 'Провайдер отклонил возврат',
    deadline_extended: 'Дедлайн уже продлевали',
    already_paid: 'Сезон уже оплачен',
    season_closed: 'Продажи сезона закрыты',
    seat_not_found: 'Место не найдено',
    seat_closed: 'Место больше не доступно',
    seat_taken: 'Место уже занято',
    seat_offered: 'Место предложено другому участнику',
    seat_offer_failed: 'Не удалось предложить место',
    seats_disabled: 'Открытые места сейчас закрыты',
    trial_disabled: 'Пробный сбор сейчас закрыт',
    gathering_too_soon: 'До сбора слишком мало времени',
    already_going: 'Участник уже идёт на этот сбор',
    reserve_not_available: 'У отряда нет места запасного',
    city_out_of_scope: 'Город не входит в зону оператора',
    city_slug_taken: 'Город с таким кодом уже есть',
    bad_timezone: 'Неизвестный часовой пояс',
    unknown_neighbor: 'Сосед не найден среди районов города',
    self_neighbor: 'Район не может быть соседом сам себе',
    district_in_use: 'Район выбран в заявках пула',
    checklist_incomplete: 'Чек-лист открытия города не пройден',
    city_already_open: 'Город уже открыт',
    city_not_open: 'Город не открыт',
    venue_not_linked: 'Площадка не привязана к аккаунту',
    venue_user_not_found: 'Сотрудник не найден: пусть один раз откроет бота',
    venue_user_exists: 'Сотрудник уже привязан',
    staff_cannot_be_venue: 'У оператора и владельца свой доступ',
    booking_not_found: 'Бронь не найдена',
    booking_closed: 'Сбор уже прошёл или отменён — решать по этой брони больше не нужно',
    company_not_found: 'Компания не найдена',
    promo_taken: 'Промокод занят',
    promo_not_found: 'Промокод не найден',
    company_already_set: 'Аккаунт уже привязан к другой компании',
    promo_generate_failed: 'Не удалось сгенерировать промокод, попробуй ещё раз',
    duplicate_district_slug: 'Коды районов должны быть уникальны',
    flag_not_found: 'Флаг не найден',
    flag_resolved: 'Флаг уже разобран',
    user_deleted: 'Аккаунт удалён',
    proposal_broken: 'Предложение повреждено: кандидаты или ячейка не найдены',
    proposal_resolved: 'Предложение уже разобрано',
    proposal_stale: 'Кандидаты уже не в пуле — запусти подбор заново',
    notification_not_found: 'Уведомление не найдено',
    already_sent: 'Уведомление уже отправлено',
    no_chat: 'К отряду не привязана группа',
    other_city: 'Соперник из другого города',
    activity_mismatch: 'У сборов разные активности',
    opponent_set: 'У сбора уже есть соперник',
    // Stage 17, «Мои сборы» (ADR-35): заявка из разовых мест и формат турнира
    time_conflict: 'Позиция пересекается по времени с другим сбором человека',
    single_limit: 'У человека без сезона исчерпан лимит разовых мест',
    single_limit_gathering: 'На сборе не осталось разовых мест: доля разовых исчерпана или капитан закрыл сбор',
    hold_expired: 'Бронь заявки истекла, места вернулись на витрину',
    feature_disabled: 'Выключено в настройках: «Мои сборы» (feature.myGatherings)',
    singles_closed: 'Капитан закрыл сбор для разовых мест',
    squad_not_launched: 'Отряд ещё набирается: разовые места продаются только в запущенном отряде',
    booking_items_limit: 'В заявке больше позиций, чем разрешает booking.maxItems',
    cancel_too_late: 'До сбора меньше порога booking.cancelFullRefundHours — отмена без возврата',
    credited_to_season:
      'Позиция зачтена в заказ сезона (не отменён) — выписка с возвратом закрыта до решения владельца (вопрос 8)',
    in_booking: 'На этот сбор в живой брони участника уже есть другая позиция',
    gathering_closed: 'Сбор уже прошёл или отменён',
    format_locked: 'Формат не меняется: турнир начался или на него уже записались',
    format_mismatch: 'У сборов пары разные форматы — сначала выбери один',
    not_tournament: 'Формат задаётся только турниру',
    capacity_out_of_range:
      'Вместимость — от размера отряда до вместимости площадки; без площадки — не больше отряда',
    venue_activity_mismatch: 'Площадка не принимает эту активность',
    gathering_out_of_season: 'Дата вне сезона: доп. сбор ставится в пределах сезона ± неделя',
    seat_held_by_booking: 'Разовое место держит заявка — его снимает бронь, выписка или отмена сбора',
    bundle_item: 'Позиция закрывается вместе с заявкой: закройте заявку или верните деньги по позиции',
  },
} as const;

export function fmt(template: string, params: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(params[key] ?? `{${key}}`));
}

/**
 * Русское склонение после числа: «1 бронь», «3 брони», «6 броней». То же правило, что `plural`
 * в `apps/web/src/lib/texts.ts`: админка и Mini App не делят модули, а хелпер — одна функция.
 */
export function plural(n: number, forms: readonly [string, string, string]): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return `${n} ${forms[2]}`;
  if (last > 1 && last < 5) return `${n} ${forms[1]}`;
  if (last === 1) return `${n} ${forms[0]}`;
  return `${n} ${forms[2]}`;
}
